import React, { useState, FC, Fragment, useEffect } from 'react';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Action } from 'models/action';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import ActionFilter from './action-filter.component';
import AddActionSectionDialog from './action-section-dialog/add-action-section-dialog.component';
import { useActionDialog } from 'hooks/useActionDialog';
import { ActionSet } from 'models/action-set';

type ActionProps = {
    actionSet: ActionSet;
    actionSetId: string;
    reFetchActionSet?: (actionSetId: string) => void;
};

const ActionSection: FC<ActionProps> = ({ actionSet, actionSetId, reFetchActionSet }) => {
    const { t: translate } = useTranslation([
        'client-directory',
        'common',
        'orders',
        'restoration-types',
        'manage-actions',
    ]);
    const [actions, setActions] = useState<Action[]>([]);
    const [sortBy, setSortBy] = useState('number');
    const [sortAscending, setSortAscending] = useState(true);
    const [_, setFilters] = useState<{
        category?: string;
        subCategory?: string;
        action?: string;
    }>({});
    const { isOpen, currentAction, openModal, closeModal, handleSaveAction, handleDeleteAction } = useActionDialog();

    const renderCheck = (condition: boolean) => (condition ? '✔' : '\u00A0');

    useEffect(() => {
        if (actionSet.actions) {
            const sorted = orderBy(actionSet.actions, [sortBy], [sortAscending ? 'asc' : 'desc']);
            setActions(sorted);
        }
    }, [actionSet.actions, sortBy, sortAscending]);

    const onAddEditAction = (action?: Action) => {
        openModal(action || ({ actionSet: actionSetId } as Action));
    };

    const handleSort = (field: string) => {
        if (sortBy === field) {
            setSortAscending(!sortAscending);
        } else {
            setSortBy(field);
            setSortAscending(true);
        }
    };

    const handleFilterChange = (type: string, value: string) => {
        setFilters((prev) => {
            const updated = { ...prev, [type]: value || undefined };

            // if category and action filters are cleared, reset to all actions
            const noFilters = !updated.category && !updated.action;
            if (noFilters) {
                setActions(actionSet.actions || []);
                return {};
            }

            const filteredActions = actionSet.actions?.filter((action) => {
                let matches = true;

                if (updated.category) {
                    matches = matches && action.mainCategory?._id === updated.category;
                }

                if (updated.subCategory) {
                    matches = matches && action.subCategory?.some((sub) => sub._id === updated.subCategory);
                }

                if (updated.action) {
                    matches =
                        matches &&
                        (action.name.toLowerCase().includes(updated.action.toLowerCase()) ||
                            action.number.toLowerCase().includes(updated.action.toLowerCase()));
                }

                return matches;
            });

            setActions(filteredActions || []);
            return updated;
        });
    };

    const headers = [
        { key: 'number', label: 'orders:ACTION' },
        { key: 'name', label: 'orders:NAME' },
        { key: 'mainCategory', label: 'orders:MAIN_CAT' },
        { key: 'subCategory', label: 'orders:SUB_CAT' },
        { key: 'denthubRestorationType', label: 'manage-actions:DENTHUB_RESTORATION_TYPE' },
        { key: 'materialGroup', label: 'orders:MATERIAL' },
        'COLOR_SHADE',
        'DESIGN',
        'CONTACT_TYPE',
        'OCCLUSION',
        'PONTIC_DESIGN',
        'RETAINMENT',
        'IMPLANT_STEPS',
        'DENTURE_STEPS',
        'TESTING_STEPS',
        'CLASP_TEETH',
        'CLASP_MATERIAL',
        'ORTH_COLORS',
        'FACIAL_LOOP',
        'CLASP_TYPES',
        'SPRING_TYPES',
        'SPRING_TEETH',
        'EXPANSION_SCREW',
        'PROTRUSION',
        'COST_PER_TOOTH',
        'NO_OF_UNITS',
        'QUOTABLE',
    ];

    const handleRefresh = () => {
        if (reFetchActionSet) {
            reFetchActionSet(actionSetId);
        }
    };

    return (
        <Fragment>
            <ActionFilter
                categories={actionSet.categories || []}
                onAddAction={onAddEditAction}
                onFilterChange={handleFilterChange}
            />
            <Box>
                <Typography color="red">
                    {translate('orders:RED')} = {translate('orders:MANDATORY')}
                </Typography>
            </Box>
            <Box maxWidth="92vw">
                <Paper variant="outlined" sx={{ mb: 2 }}>
                    <TableContainer sx={{ maxHeight: '70vh' }}>
                        <Table size="small" stickyHeader>
                            <TableHead>
                                <TableRow>
                                    {headers.map((header) => {
                                        if (typeof header === 'object') {
                                            return (
                                                <TableCell key={header.key}>
                                                    {header.key === 'number' ? (
                                                        <Tooltip title={`Sort by ${translate(header.label)}`}>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    cursor: 'pointer',
                                                                }}
                                                                onClick={() => handleSort(header.key)}
                                                            >
                                                                <Typography variant="caption">
                                                                    {translate(header.label)}
                                                                </Typography>
                                                                {sortBy === header.key &&
                                                                    (sortAscending ? (
                                                                        <ArrowDropUpIcon fontSize="small" />
                                                                    ) : (
                                                                        <ArrowDropDownIcon fontSize="small" />
                                                                    ))}
                                                            </Box>
                                                        </Tooltip>
                                                    ) : (
                                                        <Typography variant="caption">
                                                            {translate(header.label)}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                            );
                                        }
                                        return (
                                            <TableCell
                                                key={header}
                                                sx={{
                                                    writingMode: 'vertical-rl',
                                                    textAlign: 'right',
                                                    fontWeight: 1,
                                                    fontSize: '11px',
                                                    width: '5px',
                                                    padding: '5px 0',
                                                }}
                                            >
                                                {translate(`orders:${header}`)}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {actions?.map((action) => (
                                    <TableRow
                                        key={action._id}
                                        hover
                                        sx={{ cursor: 'pointer' }}
                                        onClick={() => onAddEditAction(action)}
                                    >
                                        <TableCell sx={{ fontSize: '11px', border: '1px #f2f2f2 solid' }}>
                                            {action.number}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '11px', border: '1px #f2f2f2 solid' }}>
                                            {action.name}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '11px', border: '1px #f2f2f2 solid' }}>
                                            {action.mainCategory?.name}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '11px', border: '1px #f2f2f2 solid' }}>
                                            {action.subCategory?.map(
                                                (s, i) => `${s.name}${i < action.subCategory.length - 1 ? ', ' : ''}`,
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ fontSize: '11px', border: '1px #f2f2f2 solid' }}>
                                            {action.denthubRestorationType
                                                ? translate(`restoration-types:${action.denthubRestorationType}`)
                                                : '-'}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: action.settings?.material?.mandatory ? 'red' : 'inherit',
                                                fontSize: '11px',
                                                border: '1px #f2f2f2 solid',
                                            }}
                                        >
                                            {action.materialGroup?.name || translate('orders:NONE')}
                                        </TableCell>

                                        {[
                                            'colorOrShade',
                                            'design',
                                            'contactType',
                                            'occlusion',
                                            'ponticDesign',
                                            'retainment',
                                            'implantSteps',
                                            'dentureSteps',
                                            'testingSteps',
                                            'claspTeeth',
                                            'claspMaterial',
                                            'orthColor',
                                            'facialLoop',
                                            'claspType',
                                            'springType',
                                            'springTeeth',
                                            'expansionScrew',
                                            'protrusion',
                                        ].map((key) => {
                                            const setting = (action.settings as any)?.[key];
                                            const isMandatory = setting?.mandatory;
                                            const isIncluded = setting?.include;
                                            return (
                                                <TableCell
                                                    key={key}
                                                    sx={{
                                                        color: isMandatory
                                                            ? 'red'
                                                            : isIncluded
                                                              ? 'green'
                                                              : 'text.secondary',
                                                        textAlign: 'center',
                                                        fontSize: '11px',
                                                        width: '5px',
                                                        padding: 0,
                                                        border: '1px #f2f2f2 solid',
                                                    }}
                                                >
                                                    {renderCheck(isMandatory || isIncluded)}
                                                </TableCell>
                                            );
                                        })}

                                        <TableCell
                                            sx={{
                                                color: action.costPerTooth ? 'green' : 'text.secondary',
                                                fontSize: '11px',
                                                border: '1px #f2f2f2 solid',
                                            }}
                                        >
                                            {renderCheck(action.costPerTooth)}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                color: action.settings?.numberOfUnits?.mandatory
                                                    ? 'red'
                                                    : action.settings?.numberOfUnits?.include
                                                      ? 'green'
                                                      : 'text.secondary',
                                                textAlign: 'center',
                                                fontSize: '11px',
                                                border: '1px #f2f2f2 solid',
                                            }}
                                        >
                                            {renderCheck(
                                                action.settings?.numberOfUnits?.include ||
                                                    action.settings?.numberOfUnits?.mandatory,
                                            )}
                                            {action.settings?.numberOfUnits?.isInteger ? ' INT' : ''}{' '}
                                            {action.settings?.numberOfUnits?.header || ''}
                                        </TableCell>

                                        <TableCell
                                            sx={{
                                                color: action.quotable ? 'green' : 'text.secondary',
                                                textAlign: 'center',
                                                fontSize: '11px',
                                                width: '5px',
                                                padding: 0,
                                                border: '1px #f2f2f2 solid',
                                            }}
                                        >
                                            {renderCheck(action.quotable || false)}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Box>
            <AddActionSectionDialog
                open={isOpen}
                onClose={closeModal}
                action={currentAction}
                savedActions={actions}
                categories={actionSet.categories || []}
                materials={actionSet.materialGroups || []}
                onSave={handleRefresh}
                onCreate={handleSaveAction}
                onDelete={handleDeleteAction}
            />
        </Fragment>
    );
};
export default ActionSection;
